import { generatePath } from 'react-router';
import { all, call, fork, put, select, take, takeLatest } from 'redux-saga/effects';

import { STATUS } from 'constants/ChatConstants';
import { MODULES } from 'constants/ModuleConstants';
import { PATH } from 'constants/RoutesConstants';

import { putAndWait } from 'modules/app/sagas';
import { scopeToPage } from 'modules/app/sagas/utils';
import { simpleRequestsLoader } from 'modules/app/utils';

import * as types from 'modules/campaignFiles/actionTypes';
import { getCampaignFileSocketParameters, getCampaignFilesParams } from 'modules/campaignFiles/selectors';

import { DETAILS_SOURCE } from 'modules/campaigns/constants';
import { getSelectedCampaignId } from 'modules/campaigns/selectors/details';
import { patchConversationStatus } from 'modules/chat/actions/dashboard';
import { fetchMessages } from 'modules/chat/actions/details';
import { getMessageEntityMapping } from 'modules/chat/utils';
import { navigate } from 'modules/router/navigate';
import { SOCKET_TOPIC } from 'modules/sockets/constants';
import { WBUnsubscribe, WSSubscribe } from 'modules/sockets/utils.middleware';
import { createNotification } from 'modules/toast-notifications/actions';
import { canAccessTo } from 'modules/users/selectors/currentUser';
import { humanize, ucFirst } from 'utils/global';
import { createDictionary } from 'utils/ObjectUtils';

import campaignFileBuckSaga from './bulk.saga';
import { fetchCustomer } from '../../../actions/customerActions';
import {
  fetchCampaignFile,
  fetchCampaignFiles,
  patchCampaignFileFailure,
  patchCampaignFileSuccess,
} from '../actions/core';
import { DRAWER_DETAILS_TARGET } from '../constants';

const PATCH_DESCRIPTOR = createDictionary(
  {
    handled: {
      success: {
        id: 'page.campaigns.patchCampaignFile.success',
        defaultMessage: 'The campaign has been marked as handled with success.',
      },
      failure: {
        id: 'page.campaigns.patchCampaignFile.failure',
        defaultMessage: 'An error occurred while marking as handled the campaign file.',
      },
    },
  },
  {
    success: {
      id: 'page.campaigns.campaignFile.update.success',
      defaultMessage: 'The campaign file has been updated with success.',
    },
    failure: {
      id: 'page.campaigns.campaignFile.update.failure',
      defaultMessage: 'An error occurred while editing the campaign file.',
    },
  }
);

const patchSuccessNotification =
  type =>
    (_, { meta: { field } }) =>
      PATCH_DESCRIPTOR[field][type];

/** @type {Record<string, import("modules/app/types").SimpleRequestConfig>} */
const simpleRequests = {
  [types.PATCH_CAMPAIGN_FILE]: {
    callbacks: {
      success: (payload, meta, { payload: originalPayload }) =>
        patchCampaignFileSuccess({ ...payload, campaignFileId: originalPayload.campaignFileId }, meta),
      failure: (error, meta, { payload }) => patchCampaignFileFailure(payload.campaignFileId, error, meta),
    },
    notifications: {
      success: patchSuccessNotification('success'),
      failure: {
        id: 'page.campaigns.patchCampaignFile.failure',
        defaultMessage: 'An error occurred while marking as handled the campaign file.',
      },
    },
  },
  [types.CREATE_FILE]: {
    notifications: {
      success: {
        id: 'page.campaigns.createFile.success',
        defaultMessage: 'The file has been created with success.',
      },
      failure: () => undefined,
    },
  },
  [types.CREATE_CHECKLISTS_REQUEST]: {},
  [types.APPROVE_MANUALLY_REQUEST]: {}
};

function* refreshCampaignFiles() {
  const params = yield select(getCampaignFilesParams);
  const campaignId = yield select(getSelectedCampaignId);
  yield put(fetchCampaignFiles(campaignId, { ...params, page: 0 }, { source: DETAILS_SOURCE }));
}

function* openDrawerWorker({ payload: campaignFileId }) {
  const campaignId = yield select(getSelectedCampaignId);
  const { payload: campaignFile, error } = yield putAndWait(
    fetchCampaignFile(campaignId, campaignFileId, { source: DRAWER_DETAILS_TARGET })
  );

  if (!error) {
    // Subscribe to the campaign file socket (on the CHAT topic)
    yield call(WSSubscribe, SOCKET_TOPIC.CHAT, getCampaignFileSocketParameters);
    yield putAndWait(fetchCustomer(campaignFile.customerId));
    yield putAndWait(
      fetchMessages({
        conversationId: campaignFile.conversationId,
        ...getMessageEntityMapping({ campaignFileId: campaignFile.id }),
      })
    );
    yield putAndWait(patchConversationStatus(campaignFile.conversationId, STATUS.READ, { campaignFileId }));

    yield take(types.CLOSE_DETAILS_DRAWER);

    // Unsubscribe from the campaign file socket
    yield call(WBUnsubscribe, SOCKET_TOPIC.CHAT);
  }
}

function* redirectToFile({ payload: { id, module } }) {
  const path = generatePath(MODULES[module].path, { interventionId: id });
  const haveAccess = yield select(state => canAccessTo(state, path));

  if (haveAccess) {
    yield call(navigate, path);
  }
}

function* handleFileCreationError({ payload }) {
  const { errorType } = payload.response?.data ?? {};
  if (errorType === 'INTERVENTION_ALREADY_EXISTS') {
    const message = {
      id: `page.campaigns.createFile.error.${errorType}`,
      defaultMessage: humanize(ucFirst(errorType)),
    };
    yield put(createNotification({ type: 'warning', message, description: '' }));
  } else {
    const message = {
      id: 'page.campaigns.createFile.failure',
      defaultMessage: 'An error occurred while creating the file.',
    };
    yield put(createNotification({ type: 'error', message }));
  }
}

const REFRESH_TYPES = [
  types.SET_CAMPAIGN_FILES_FILTER,
  types.SET_CAMPAIGN_FILES_FILTERS,
  types.CLEAR_CAMPAIGN_FILES_FILTER,
  types.REMOVE_CAMPAIGN_FILES_FILTER,
];

function* onDetailsWorker() {
  yield all(simpleRequestsLoader(simpleRequests));

  yield takeLatest(REFRESH_TYPES, refreshCampaignFiles);
  yield takeLatest(types.CREATE_FILE_SUCCESS, redirectToFile);
  yield takeLatest(types.OPEN_DETAILS_DRAWER, openDrawerWorker);
  yield takeLatest(types.CREATE_FILE_FAILURE, handleFileCreationError);
}

export default function* campaignsDashboardSaga() {
  yield scopeToPage([PATH.CAMPAIGNS_DETAILS], onDetailsWorker);
  yield fork(campaignFileBuckSaga);
}
